import { Command, tabActions } from '@store/slices';
import { handleCallError, NodeTraceEvent, RunWorkflowParameters } from '../run';
import { Message } from '@common/Node/NodeSidebar/NodeInput/NodeMessageArray';
import { Translation } from '@components/Flow';
import { RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';
import { State, Tab } from '@store/index';

export const createTrace = (id: string, type: string, name: string, icon: string) => ({
  id,
  type,
  background: '',
  icon,
  name,
  description: null,
  showCollapse: false,
  collapsed: true,
});

export const eventHandler = async (
  id: string,
  type: string,
  value: any,
  dispatch: any,
  commands: Command[],
  modules: Tab[],
  getState: () => unknown,
  parent_workflow_id: string,
  theme: string,
  abortController: AbortController,
  rtc?: RTC
) => {
  // first event received after starting workflow
  if (type === NodeTraceEvent.WorkflowStart) {
    const trace = createTrace(id, type, 'Workflow Start', 'workflow');
    const run_id = value?.run_id;
    const workflow_id = value?.workflow_id;

    dispatch(tabActions.startWorkflowRun({ run_id, workflow_id }));
    dispatch(tabActions.addTrace({ node_id: null, workflow_id, trace }));

    // initiate the inbound call to trigger the workflow, if workflow has inbound call trigger
    if (rtc) {
      if (!rtc.destinationNumber) {
        throw new Error('Failed to detect destination number. Please refresh if this issue persists.');
      }

      await rtc.connect(); // This will throw if there's an error in connect()
      rtc.dial({ callId: value?.run_id, destinationNumber: rtc.destinationNumber }).catch((error) => {
        const trigger = commands.find((command) => command.config.trigger);
        const state = getState() as State;

        if (error?.type === 'media') abortController?.abort();
        return handleCallError(error, state.tabs.active.run, trigger, dispatch);
      });
    }
  }

  // update on the status of a node: pending, processing, failed, completed
  if (type === NodeTraceEvent.NodeStatus) {
    handleNodeStatus(id, value, dispatch, commands, modules, theme);
  }

  if (type === NodeTraceEvent.NodeJumpIncrement) {
    const { workflow_id, node_id, increment, max_jumps, target_node_id } = value;
    dispatch(tabActions.addJumpTraceIncrement({ workflow_id, node_id, increment, max_jumps, target_node_id }));
  }

  if (type === NodeTraceEvent.NodeReset) {
    const { workflow_id, node_id } = value;

    const command = commands.find((command) => command.node.id === node_id);
    const commandNodeId = command?.node.id;

    const strokeColor = theme === 'dark' ? '#ffffff4a' : '#00000016';
    const commandTargetConnections =
      command?.connections.filter((connection) => connection.edge.source === commandNodeId) || [];

    const commandTargetEdgeIds = commandTargetConnections.map((connection) => connection.edge.id);

    // set the downstream edges back to the workflow start color
    dispatch(tabActions.setEdgesColor({ edgeIds: commandTargetEdgeIds, stroke: strokeColor }));

    // remove the traces for the node
    dispatch(tabActions.removeTrace({ workflow_id, node_id }));
  }

  // the result of a condition being evaluated in the switch or branch nodes
  if (type === NodeTraceEvent.NodeConditionalExecution) {
    handleNodeConditionalExecution(value, dispatch, commands, theme);
  }

  // the output of a node when it is done executing
  if (type === NodeTraceEvent.NodeOutput) {
    const { node_id, data } = value;
    dispatch(tabActions.refreshDynamicNodeData({ node_id, output: data, moduleId: value?.moduleId }));
  }

  // the total execution time of the node
  if (type === NodeTraceEvent.NodeDuration) {
    const { node_id, duration, workflow_id } = value;
    dispatch(tabActions.updateTraceNodeDuration({ node_id, duration, workflow_id }));
  }

  if (
    type === NodeTraceEvent.WebhookPending ||
    type === NodeTraceEvent.WebhookReceived ||
    type === NodeTraceEvent.WebhookCancelled
  ) {
    handleNodeWebhook(value, type, dispatch, commands, theme);
  }

  // the end of the workflow
  if (type === NodeTraceEvent.WorkflowEnd) {
    const trace = createTrace(id, type, 'Workflow End', 'workflow');
    const workflow_id = value;
    dispatch(tabActions.stopWorkflowRun());
    dispatch(tabActions.addTrace({ node_id: null, workflow_id, trace }));
  }

  // an error was thrown during workflow execution
  if (type === NodeTraceEvent.WorkflowError) {
    handleWorkflowError(id, value, dispatch);
  }

  // if a node throws an error during execution
  if (type === NodeTraceEvent.NodeError) {
    handleNodeError(value, dispatch);
    dispatch(tabActions.updateTraceEvent({ event: 'endCall' as const, field: 'caught', value: true }));
  }

  // if the workflow supports chat, the backend will send the chat configuration (message, bot_id, templates, etc)
  if (type === NodeTraceEvent.NodeCompletionChat) {
    handleNodeCompletionChat(value, dispatch);
  }

  // if a completion is being streamed, this event catches each token streamed
  if (type === NodeTraceEvent.NodeCompletionToken) {
    dispatch(tabActions.chatAddMessageToken({ id, value }));
  }

  // receives this event if a tool is called
  if (type === NodeTraceEvent.NodeCompletionToolSelected) {
    handleNodeCompletionToolSelected(id, value, dispatch, commands);
  }

  // receives this event if a tool is retried due to output errors
  if (type === NodeTraceEvent.NodeCompletionToolRetry) {
    dispatch(tabActions.chatAddMessageRetryTool({ id, value }));
  }

  // receives this event when a tool called is being executed
  if (type === NodeTraceEvent.NodeCompletionToolInvoked) {
    const workflow_id = value?.workflow_id;
    const node_id = value?.node_id;

    dispatch(tabActions.chatAddMessageUpdateTool({ id, node_id, workflow_id, value }));
  }

  // receives this event when a tool call is finished, returns the output details
  if (type === NodeTraceEvent.NodeCompletionTool) {
    handleNodeCompletionTool(id, value, dispatch, commands);
  }

  // receives this event if an unfixable error occurs during tool execution
  // if (type === NodeTraceEvent.NodeCompletionToolError) {
  //   handleNodeCompletionToolError(id, value, dispatch, commands);
  // }

  // receives this event when a document is matched during a tool execution
  if (type === NodeTraceEvent.NodeCompletionMatches) {
    dispatch(tabActions.chatAddMessageMatches({ id, value }));
  }

  // receives this event when a document is selected during a tool execution
  if (type === NodeTraceEvent.NodeCompletionDocuments) {
    dispatch(tabActions.chatAddMessageDocuments({ id, value }));
  }

  // receives this event if an individual node throws an error
  if (type === NodeTraceEvent.NodeCompletionError) {
    dispatch(tabActions.chatAddMessageError({ id, value }));
  }

  // recevies this event when the completion is done being streamed
  if (type === NodeTraceEvent.NodeCompletionComplete) {
    handleNodeCompletionComplete(id, value, dispatch);
  }

  if (type === NodeTraceEvent.NodeCompletionToolApproval) {
    const workflow_id = value?.workflow_id;
    const node_id = value?.node_id;

    dispatch(tabActions.chatAddMessageAddToolApproval({ id, node_id, workflow_id, value }));
  }

  if (type === NodeTraceEvent.CallStart) {
    dispatch(tabActions.updateTraceEvent({ event: 'openCall' as const, field: 'caught', value: true }));
  }

  if (type === NodeTraceEvent.CallEnd) {
    dispatch(tabActions.updateTraceEvent({ event: 'endCall' as const, field: 'caught', value: true }));
  }

  if (type === NodeTraceEvent.MessagingResponse) {
    dispatch(tabActions.messagingAddMessage(value));
    dispatch(tabActions.updateTraceEvent({ event: 'openMessaging' as const, field: 'caught', value: true }));
  }
};

export const createRequestBody = (parameters: RunWorkflowParameters, translation: Translation) => {
  const body = parameters?.body;
  const moduleParameters = parameters?.moduleParameters;
  const messageParameters = parameters?.messageParameters;

  if (messageParameters) {
    return { parameters: messageParameters, translation };
  }

  if (body && Object.keys(body).length > 0) {
    return { ...body, translation };
  }

  if (moduleParameters) {
    return { parameters: moduleParameters, translation };
  }

  return { translation };
};

const handleNodeWebhook = (value: any, type: NodeTraceEvent, dispatch: any, commands: Command[], theme: string) => {
  const { node_id, workflow_id, webhook } = value;

  dispatch(tabActions.addTraceWebhook({ node_id, workflow_id, type, webhook }));

  // update execution edge when webhook received
  if (type === NodeTraceEvent.WebhookReceived) {
    updateWebhookEdgeStyles(webhook, node_id, commands, dispatch, theme);
  }
};

const handleNodeStatus = (
  id: string,
  value: any,
  dispatch: any,
  commands: Command[],
  modules: Tab[],
  theme: string
) => {
  const { node_id, status, name, workflow_id, isModule = false } = value;

  const moduleNode = isModule ? modules?.find((mod) => mod.flow?.flow_id === workflow_id) : undefined;
  const inputCommands = moduleNode ? moduleNode?.commands : commands;

  const nodeCommand = inputCommands.find((command) => command.node.id === node_id);
  const nodeState = nodeCommand?.state;
  const displayFieldId = nodeCommand?.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (nodeState?.[displayFieldId] as string) : null;

  const trace = {
    id,
    type: NodeTraceEvent.NodeStatus,
    icon: nodeCommand?.config.icon || '',
    name: name || displayTitle || nodeCommand?.config.title || '',
    background: nodeCommand?.config.background || '',
    description: null,
    showCollapse: false,
    collapsed: true,
    metadata: {
      status,
      duration: null,
      node_id,
      workflow_id,
      timers: [],
      output: '',
      webhooks: [],
      cancelled_webhooks: [],
    },
  };

  dispatch(tabActions.addTrace({ node_id, workflow_id, trace }));
  dispatch(tabActions.setNodeRunStatus(value));

  /* we handle the updating of webhook execution edges differently
     the call.initiated node is the only node that behaves like non-webhook nodes */

  const isWebhookExecution =
    nodeCommand?.config?.webhooks &&
    nodeCommand?.config?.webhooks?.length > 0 &&
    nodeCommand?.config?.command_id !== 'call.initiated';

  if (status === 'completed' && !isWebhookExecution && !isModule) {
    updateEdgeStyles(node_id, commands, dispatch, false, theme);
  }
};

const handleNodeConditionalExecution = (value: any, dispatch: any, commands: Command[], theme: string) => {
  const executionLineColor = theme === 'dark' ? '#ffffff' : '#000000';
  const dataLineColor = theme === 'dark' ? '#5858FF' : '#3434EF';

  const { node_id, trueExecutions } = value;
  const command = commands.find((command) => command.node.id === node_id);
  const sourceDataConnections = command?.connections.filter(
    (connection) => connection.target_anchor.variant === 'data' && connection.edge.source === node_id
  );

  const executedDataConnections =
    sourceDataConnections?.filter((connection) =>
      trueExecutions.some((trueExec: any) => trueExec.target_node_id === connection.edge.target)
    ) || [];

  for (const connection of executedDataConnections) {
    dispatch(tabActions.setEdgeStyle({ connection: connection.edge, field: 'stroke', value: dataLineColor }));
    dispatch(tabActions.setEdgeAnimating({ connection: connection.edge, value: true }));
  }

  for (const connection of trueExecutions) {
    dispatch(
      tabActions.setEdgeStyle({
        connection: {
          ...connection,
          source: connection.source_node_id,
          target: connection.target_node_id,
        },
        field: 'stroke',
        value: executionLineColor,
      })
    );
  }
};

const handleNodeError = (value: any, dispatch: any) => {
  dispatch(tabActions.addNodeError(value));
};

const handleWorkflowError = (id: string, value: any, dispatch: any) => {
  const error = value?.error;
  const workflow_id = value?.workflow_id;

  console.log('Workflow Error', error);
  const errorMessage = error?.detail || 'An unexpected error occurred while executing the workflow.';
  dispatch(tabActions.updateTraceEvent({ event: 'openParentTab' as const, field: 'caught', value: true }));

  // dont throw a workflow error if we have a node error
  if (error?.nodeErrors?.length > 0) {
    return dispatch(tabActions.stopWorkflowRun());
  }

  const trace = {
    id,
    type: NodeTraceEvent.WorkflowError,
    background: '#f35c5c',
    icon: 'workflow',
    name: 'Workflow Error',
    description: {
      reason: errorMessage,
    },
    showCollapse: false,
    collapsed: true,
  };

  dispatch(tabActions.addTrace({ node_id: null, workflow_id, trace }));
  dispatch(tabActions.errorWorkflowRun(errorMessage));
};

const handleNodeCompletionChat = (value: any, dispatch: any) => {
  dispatch(tabActions.updateTraceEvent({ event: 'openChat' as const, field: 'caught', value: true }));
  dispatch(tabActions.chatAddWorkflowChat(value));

  if (value?.messages?.messages) {
    const allMessages = value?.messages?.messages?.filter((item: Message) => item.role !== 'system') || [];
    const lastUser = allMessages[allMessages.length - 1];
    const prevMessages = allMessages.slice(0, allMessages.length - 1);

    dispatch(
      tabActions.chatLoadMessages({
        node_id: value.node_id,
        messages: prevMessages,
      })
    );

    dispatch(
      tabActions.chatAddMessage({
        id: value.message_id,
        node_id: value.node_id,
        workflow_id: value.workflow_id,
        bot_id: value.bot_id,
        message: lastUser.content,
      })
    );
  } else {
    dispatch(
      tabActions.chatAddMessage({
        id: value.message_id,
        node_id: value.node_id,
        workflow_id: value.workflow_id,
        bot_id: value.bot_id,
        message: value.query,
      })
    );
  }
};

const handleNodeCompletionToolSelected = (id: string, value: any, dispatch: any, commands: Command[]) => {
  const node_id = value?.node_id;
  const workflow_id = value?.workflow_id;

  const toolNode = commands.find((command) => command.node.id === value.node_id);

  dispatch(tabActions.updateTraceEvent({ event: 'openTools' as const, field: 'caught', value: true }));

  const trace = {
    type: NodeTraceEvent.NodeCompletionToolSelected,
    icon: toolNode?.config.icon || '',
    name: toolNode?.config.identifier || '',
    background: toolNode?.config.background || '',
    description: null,
    showCollapse: false,
    collapsed: true,
    metadata: {
      status: 'processing',
      duration: null,
      node_id: node_id || '',
      workflow_id: workflow_id || '',
      timers: [],
      output: '',
      webhooks: [],
      cancelled_webhooks: [],
    },
  };

  dispatch(tabActions.addTrace({ node_id, workflow_id, trace }));

  const tool = { id, value, node_id, workflow_id };
  dispatch(tabActions.chatAddMessageToolSelected(tool));
};

const handleNodeCompletionTool = (id: string, value: any, dispatch: any, commands: Command[]) => {
  const node_id = value?.node_id;
  const workflow_id = value?.workflow_id;

  const tool = { id, node_id, workflow_id, value };

  dispatch(tabActions.chatAddMessageUpdateTool(tool));

  const toolNode = commands.find((command) => command.node.id === value.node_id);

  const trace = {
    type: NodeTraceEvent.NodeCompletionTool,
    icon: toolNode?.config.icon || '',
    name: toolNode?.config.identifier || '',
    background: toolNode?.config.background || '',
    description: null,
    showCollapse: false,
    collapsed: true,
    metadata: {
      status: 'completed',
      duration: null,
      node_id: node_id || '',
      workflow_id: workflow_id || '',
      timers: [],
      output: '',
      webhooks: [],
      cancelled_webhooks: [],
    },
  };

  dispatch(tabActions.addTrace({ node_id: node_id || '', workflow_id: workflow_id || '', trace }));
};

const handleNodeCompletionComplete = (id: string, value: any, dispatch: any) => {
  const { show_help_action, show_feedback } = parseFeedback(value);
  dispatch(tabActions.chatUpdateMessage({ id, field: 'show_help_action', value: show_help_action }));
  dispatch(tabActions.chatUpdateMessage({ id, field: 'show_feedback', value: show_feedback }));
  dispatch(tabActions.chatUpdateMessage({ id, field: 'state', value: 'complete' }));
};

const parseFeedback = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return {
      show_help_action: parsed.show_help_action,
      show_feedback: parsed.show_feedback,
    };
  } catch (e) {
    return {
      show_help_action: true,
      show_feedback: true,
    };
  }
};

const getCommandEdges = (
  node_id: string,
  commands: Command[],
  variant: 'execution' | 'data' | 'plugin' | 'webhook_execution',
  direction: 'source' | 'target' | 'both' = 'both'
) => {
  const command = commands.find((command) => command.node.id === node_id);
  if (!command?.connections?.length || command.connections.length <= 0) return [];

  const connections = command.connections.filter((connection) => {
    const isValidVariant = connection.target_anchor.variant === variant;
    if (direction === 'source') return isValidVariant && connection.edge.source === node_id;
    if (direction === 'target') return isValidVariant && connection.edge.target === node_id;
    return isValidVariant && (connection.edge.source === node_id || connection.edge.target === node_id);
  });

  return connections.map((connection) => connection.edge) || [];
};

const updateEdgeStyles = (
  node_id: string,
  commands: Command[],
  dispatch: any,
  isLogicCommand: boolean,
  theme: string
) => {
  const executionLineColor = theme === 'dark' ? '#ffffff' : '#000000';
  const dataLineColor = theme === 'dark' ? '#5858FF' : '#3434EF';
  const pluginLineColor = '#ff7442ae';

  const executionEdges = getCommandEdges(node_id, commands, 'execution', 'source');
  const webhookExecutionEdges = getCommandEdges(node_id, commands, 'webhook_execution', 'source').filter(
    (edge) => edge.sourceHandle === 'call.initiated'
  );

  const allExecutionEdges = executionEdges.concat(webhookExecutionEdges);

  const dataEdges = getCommandEdges(node_id, commands, 'data', 'source');
  const pluginEdges = getCommandEdges(node_id, commands, 'plugin');

  for (const connection of allExecutionEdges) {
    dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: executionLineColor }));
    dispatch(tabActions.setEdgeAnimating({ connection, value: false }));
  }

  if (!isLogicCommand) {
    for (const connection of dataEdges) {
      const sourceCommand = commands.find((command) => command.node.id === connection.source);

      if (sourceCommand?.config?.command_id === 'call.initiated') {
        if (connection?.sourceHandle?.includes('call.initiated')) {
          dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: dataLineColor }));
          dispatch(tabActions.setEdgeAnimating({ connection, value: true }));
        }
      } else {
        dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: dataLineColor }));
        dispatch(tabActions.setEdgeAnimating({ connection, value: true }));
      }
    }
  }

  for (const connection of pluginEdges) {
    dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: pluginLineColor }));
  }
};

const updateWebhookEdgeStyles = (
  webhook: string,
  node_id: string,
  commands: Command[],
  dispatch: any,
  theme: string
) => {
  const executionLineColor = theme === 'dark' ? '#ffffff' : '#000000';
  const dataLineColor = theme === 'dark' ? '#5858FF' : '#3434EF';

  const executionEdges = getCommandEdges(node_id, commands, 'webhook_execution', 'source');
  const dataEdges = getCommandEdges(node_id, commands, 'data', 'source');

  const webhookExecutionEdges = executionEdges.filter((edge) => edge.sourceHandle === webhook);
  const webhookDataEdges = dataEdges.filter((edge) => edge.sourceHandle === `${webhook}.output_data`);

  for (const connection of webhookExecutionEdges) {
    dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: executionLineColor }));
    dispatch(tabActions.setEdgeAnimating({ connection, value: false }));
  }

  for (const connection of webhookDataEdges) {
    dispatch(tabActions.setEdgeStyle({ connection, field: 'stroke', value: dataLineColor }));
    dispatch(tabActions.setEdgeAnimating({ connection, value: true }));
  }
};
